import { cookies } from '@jotta/utils/cookie'
import type { BinaryReadOptions, Message } from '@bufbuild/protobuf'
import { base64ToBytes, bytesToBase64 } from '@jotta/utils/base64'
import type { useState } from 'react'
import type { StateCreator } from 'zustand'
import Debug from 'debug'

const debug = Debug('jotta:protocookie')

export type ProtoCookie<T> = ReturnType<typeof useState<T>>

type ProtoMessage<T extends Message<T>> = typeof Message<T> & {
  fromBinary: (bytes: Uint8Array, options?: Partial<BinaryReadOptions>) => T
}

type Options<T extends Message<T>> = {
  name: string
  maxAgeSeconds?: number
  type: ProtoMessage<T>
}

function encodeCookie(nextValue: Message<any>) {
  return bytesToBase64(nextValue.toBinary())
}

function decodeCookie<T extends Message<T>>({ name, type }: Options<T>) {
  const initialCookieValue = cookies.get(name)
  try {
    return initialCookieValue
      ? type.fromBinary(base64ToBytes(initialCookieValue))
      : undefined
  } catch (e) {
    reportError(e)
  }
}

export const persistProtoCookie = <T extends Message<T>>(
  options: Options<T>,
): StateCreator<ProtoCookie<T>> => {
  const { name, maxAgeSeconds } = options
  return (set): ProtoCookie<T> => {
    return [
      decodeCookie(options),
      setStateAction =>
        set(previousState => {
          const previousValue = previousState[0]
          const nextValue =
            typeof setStateAction === 'function'
              ? setStateAction(previousValue)
              : setStateAction
          if (nextValue) {
            debug('updating', name)
            cookies.set(name, encodeCookie(nextValue), {
              maxAge: maxAgeSeconds,
              path: '/',
            })
          } else {
            debug('removing', name)
            cookies.remove(name)
          }
          return [nextValue, previousState[1]]
        }, true),
    ]
  }
}
